import { defineStore } from 'pinia'
import type { ShopAPI } from '@/api/typeings/shops'
import { useShopStore } from '@/store/modules/payment'

export interface IntegrationsForm {
  storefrontToken: string
  storefrontApiPrivateToken: string
  facebookPixels: string[]
  tikTokPixels: string[]
  tikTokConversionAccessTokens: string[]
  newsBreakPixels: string[]
  newsBreakPixelInputType: string
  bigoPixels: string[]
  roaiPixels: string[]
  pinterestAccountIds: string[]
  pinterestConversionAccessTokens: string[]
  snapchatPixelIds: string[]
  snapchatConversionApiTokens: string[]
  smartNewsPartnerNames: string[]
  smartNewsPixelTagIds: string[]
  smartNewsAuthorization: string
  mediaGoAdIds: string[]
  mediaGoAccountNames: string[]
  mediaGoAdId: string
  mediaGoAccountName: string
  googleAdsGIds: string[]
  googleAdsConversionSendTos: string[]
  googleAdsConversionSendTo: string
  googleAdsGId: string
  isEnableGoogleAdsPostPurchaseEvent: boolean
  googlePlacesApiKey: string
  isEnableConversionAPI: boolean
  isEnableEventPixelDetails: boolean
  isEnablePostPurchaseEvent: boolean
  isEnableShippingCostInPurchaseEvents: boolean
  facebookConversionApiTokens: string[]
  isValidToken: boolean
  isValidPrivateToken: boolean
  facebookADM: string
  tikTokADM: string
  applovinPixelInputs: ApplovinInputItem[]
  applovinPixelEventKey: string
  applovinPixelId: string
  isEnableApplovinBackendReport: boolean
  postbackValuePercentage: number
  extensionCode: string
  isEnableExtensionCode: boolean
  isEnableNBConversionAPI: boolean
  isEnableBigoBackendReport: boolean
  isEnablePinterestBackendConversion: boolean
  isEnableSnapchatBackendReport: boolean
  isEnableSmartNewsBackendConversion: boolean
  isEnableMediaGoBackendConversion: boolean
  isEnableTikTokBackendReport: boolean
}

export interface ApplovinInputItem {
  productVendor: string
  productCategory: string
}

export const useSettingIntegrationsStore = defineStore('settingIntegrations', {
  state: () => ({
    integrationsForm: {
      storefrontToken: '',
      storefrontApiPrivateToken: '',
      facebookPixels: [] as string[],
      tikTokPixels: [] as string[],
      tikTokConversionAccessTokens: [] as string[],
      newsBreakPixels: [] as string[],
      newsBreakPixelInputType: 'pixel',
      bigoPixels: [] as string[],
      roaiPixels: [] as string[],
      pinterestAccountIds: [] as string[],
      pinterestConversionAccessTokens: [] as string[],
      snapchatPixelIds: [] as string[],
      snapchatConversionApiTokens: [] as string[],
      smartNewsPartnerNames: [] as string[],
      smartNewsPixelTagIds: [] as string[],
      smartNewsAuthorization: '',
      mediaGoAdIds: [] as string[],
      mediaGoAccountNames: [] as string[],
      mediaGoAdId: '',
      mediaGoAccountName: '',
      googleAdsGIds: [] as string[],
      googleAdsConversionSendTos: [] as string[],
      googleAdsConversionSendTo: '',
      googleAdsGId: '',
      isEnableGoogleAdsPostPurchaseEvent: false,
      googlePlacesApiKey: '',
      isEnableConversionAPI: false,
      isEnableEventPixelDetails: false,
      isEnablePostPurchaseEvent: false,
      isEnableShippingCostInPurchaseEvents: false,
      facebookConversionApiTokens: [] as string[],
      isValidToken: false,
      isValidPrivateToken: false,
      facebookADM: '',
      tikTokADM: 'Disabled',
      applovinPixelInputs: [] as ApplovinInputItem[],
      applovinPixelEventKey: '',
      applovinPixelId: '',
      isEnableApplovinBackendReport: false,
      postbackValuePercentage: 100,
      extensionCode: '',
      isEnableExtensionCode: false,
      isEnableNBConversionAPI: false,
      isEnableBigoBackendReport: false,
      isEnablePinterestBackendConversion: false,
      isEnableSnapchatBackendReport: false,
      isEnableSmartNewsBackendConversion: false,
      isEnableMediaGoBackendConversion: false,
      isEnableTikTokBackendReport: false,
    } as IntegrationsForm,
  }),

  actions: {
    setIntegrationsForm(form: Partial<IntegrationsForm>) {
      this.integrationsForm = { ...this.integrationsForm, ...form }
    },

    async saveIntegrationsForm() {
      const shopStore = useShopStore()
      await shopStore.fetchShopDetail()

      // 准备 integrations 数据，移除 storefront tokens
      const {
        storefrontToken,
        storefrontApiPrivateToken,
        googlePlacesApiKey,
        ...integrationsData
      } = this.integrationsForm

      // 准备 shopApi 数据
      let shopApi = {}
      if (shopStore.shopDetail?.shopApi) {
        try {
          shopApi = JSON.parse(shopStore.shopDetail.shopApi)
        } catch (error) {
          console.error('Error parsing shopApi:', error)
          shopApi = {}
        }
      }

      // 更新 shopApi 中的 storefront tokens
      const updatedShopApi = {
        ...shopApi,
        storefrontApiAccessToken: storefrontToken,
        storefrontApiPrivateToken: storefrontApiPrivateToken,
        googlePlacesApiKey,
      }

      // 使用原始数据保持必需的属性 保存到shops 更新接口中
      const baseParams: ShopAPI.ShopDto = {
        ...shopStore.shopDetail,
        shopApi: JSON.stringify(updatedShopApi),
        intergrations: JSON.stringify(integrationsData),
      }

      await shopStore.updateShop(baseParams)
    },
  },
})
